/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useWindowManager.ts - Hook para gestión de ventanas flotantes
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Hook personalizado para manejar ventanas flotantes multi-instancia
 * @version 2.0.0 - ACTUALIZADO: Soporte para widgets genéricos + z-index dinámico
 * @architecture WASP Frontend
 * 
 * FUNCIONALIDADES:
 * - ✅ Crear/cerrar ventanas
 * - ✅ Drag & drop
 * - ✅ Resize
 * - ✅ Z-index automático con bring-to-front
 * - ✅ Minimizar/maximizar
 * - ✅ Posicionamiento escalonado
 * - ✅ Soporte para conversaciones Y widgets
 * - ✅ Indicador visual de ventana activa
 * 
 * CAMBIOS v2.0:
 * - ✅ Agregado campo 'type' a ConversationWindow
 * - ✅ Agregado campo 'metadata' opcional
 * - ✅ Nueva función registerWindow() para widgets genéricos
 * - ✅ Nueva función getMaxZIndex() para indicadores visuales
 * - ✅ Nueva función getActiveWindowId() para tracking
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useCallback, useRef } from 'react';

// Constantes
const WINDOW_POSITION_OFFSET = 30;
const INITIAL_Z_INDEX = 1000;
const DEFAULT_WINDOW_SIZE = { width: 400, height: 600 };
const DEFAULT_BUBBLE_SIZE = { width: 60, height: 60 };

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════

export interface WindowPosition {
  x: number;
  y: number;
}

export interface WindowSize {
  width: number;
  height: number;
}

/**
 * Interface principal de ventana (actualizada v2.0)
 */
export interface ConversationWindow {
  id: string;
  conversationId: string;
  type?: 'conversation' | 'widget'; // ✅ NUEVO: tipo de ventana
  position: WindowPosition;
  size: WindowSize;
  isMinimized: boolean;
  zIndex: number;
  metadata?: any; // ✅ NUEVO: datos adicionales opcionales
}

/**
 * Configuración para registrar ventana genérica
 */
export interface RegisterWindowConfig {
  id: string;
  type: 'conversation' | 'widget';
  conversationId?: string;
  position?: WindowPosition;
  size?: WindowSize;
  metadata?: any;
}

export interface UseWindowManagerReturn {
  windows: ConversationWindow[];
  nextZIndex: number;
  openWindow: (conversationId: string) => void;
  closeWindow: (windowId: string) => void;
  minimizeWindow: (windowId: string) => void;
  maximizeWindow: (windowId: string) => void;
  updateWindowPosition: (windowId: string, position: WindowPosition) => void;
  updateWindowSize: (windowId: string, size: WindowSize) => void;
  bringToFront: (windowId: string) => void;
  isWindowOpen: (conversationId: string) => boolean;
  getWindow: (windowId: string) => ConversationWindow | undefined;
  closeAllWindows: () => void;
  registerWindow: (config: RegisterWindowConfig) => string; // ✅ NUEVO
  getMaxZIndex: () => number; // ✅ NUEVO
  getActiveWindowId: () => string | null; // ✅ NUEVO
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Generar posición escalonada para nueva ventana
 */
const generateWindowPosition = (windowCount: number): WindowPosition => {
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
  
  // Calcular posición inicial centrada
  const baseX = Math.max(50, (screenWidth - DEFAULT_WINDOW_SIZE.width) / 2);
  const baseY = Math.max(50, (screenHeight - DEFAULT_WINDOW_SIZE.height) / 2);
  
  // Escalonar las ventanas con offset
  const offsetX = (windowCount * WINDOW_POSITION_OFFSET) % 200;
  const offsetY = (windowCount * WINDOW_POSITION_OFFSET) % 150;
  
  return {
    x: baseX + offsetX,
    y: baseY + offsetY
  };
};

/**
 * Generar posición aleatoria para burbuja
 */
const generateBubblePosition = (): WindowPosition => {
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
  
  // Posición aleatoria en el borde derecho
  const x = screenWidth - DEFAULT_BUBBLE_SIZE.width - 20;
  const y = Math.random() * (screenHeight - DEFAULT_BUBBLE_SIZE.height - 100) + 50;
  
  return { x, y };
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook personalizado para gestión de ventanas flotantes
 * 
 * @returns Estado y funciones de gestión de ventanas
 * 
 * @example
 * ```tsx
 * // Para conversaciones
 * const { openWindow, closeWindow, bringToFront } = useWindowManager();
 * openWindow('56912345678');
 * 
 * // Para widgets genéricos
 * const { registerWindow, bringToFront } = useWindowManager();
 * const widgetId = registerWindow({
 *   id: 'risk-meter-widget',
 *   type: 'widget',
 *   position: { x: 20, y: 100 },
 *   size: { width: 420, height: 680 },
 *   metadata: { collapsed: false }
 * });
 * 
 * // Traer al frente al hacer click
 * const handleClick = () => bringToFront(widgetId);
 * ```
 */
export const useWindowManager = (): UseWindowManagerReturn => {
  const [windows, setWindows] = useState<ConversationWindow[]>([]);
  const [nextZIndex, setNextZIndex] = useState<number>(INITIAL_Z_INDEX);
  const windowCountRef = useRef<number>(0);

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES DE CONSULTA
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Verificar si ya hay una ventana abierta para una conversación
   */
  const isWindowOpen = useCallback(
    (conversationId: string): boolean => {
      return windows.some(w => w.conversationId === conversationId);
    },
    [windows]
  );

  /**
   * Obtener ventana por ID
   */
  const getWindow = useCallback(
    (windowId: string): ConversationWindow | undefined => {
      return windows.find(w => w.id === windowId);
    },
    [windows]
  );

  /**
   * ✅ NUEVO: Obtener el z-index máximo actual
   */
  const getMaxZIndex = useCallback((): number => {
    if (windows.length === 0) return INITIAL_Z_INDEX;
    return Math.max(...windows.map(w => w.zIndex));
  }, [windows]);

  /**
   * ✅ NUEVO: Obtener ID de la ventana activa (mayor z-index)
   */
  const getActiveWindowId = useCallback((): string | null => {
    if (windows.length === 0) return null;
    const maxZ = getMaxZIndex();
    const activeWindow = windows.find(w => w.zIndex === maxZ);
    return activeWindow?.id || null;
  }, [windows, getMaxZIndex]);

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES DE GESTIÓN
  // ───────────────────────────────────────────────────────────────────────

  /**
   * ✅ NUEVO: Registrar ventana genérica (conversación o widget)
   * 
   * @param config - Configuración de la ventana
   * @returns ID de la ventana creada
   */
  const registerWindow = useCallback(
    (config: RegisterWindowConfig): string => {
      // Verificar si ya existe (solo para conversaciones)
      if (config.type === 'conversation' && config.conversationId) {
        if (isWindowOpen(config.conversationId)) {
          const existingWindow = windows.find(w => w.conversationId === config.conversationId);
          if (existingWindow) {
            bringToFront(existingWindow.id);
            return existingWindow.id;
          }
        }
      }

      // Crear nueva ventana
      const newWindow: ConversationWindow = {
        id: config.id,
        conversationId: config.conversationId || config.id,
        type: config.type,
        position: config.position || generateWindowPosition(windowCountRef.current),
        size: config.size || DEFAULT_WINDOW_SIZE,
        isMinimized: false,
        zIndex: nextZIndex,
        metadata: config.metadata
      };

      windowCountRef.current += 1;
      setWindows(prev => [...prev, newWindow]);
      setNextZIndex(prev => prev + 1);

      console.log(`✅ Ventana registrada: ${config.id} (${config.type}) - z-index: ${nextZIndex}`);
      
      return newWindow.id;
    },
    [windows, nextZIndex, isWindowOpen]
  );

  /**
   * Abrir nueva ventana de conversación (legacy - usa registerWindow internamente)
   */
  const openWindow = useCallback(
    (conversationId: string): void => {
      // Verificar si ya está abierta
      if (isWindowOpen(conversationId)) {
        const existingWindow = windows.find(w => w.conversationId === conversationId);
        if (existingWindow) {
          bringToFront(existingWindow.id);
        }
        return;
      }

      // Tamaño y posición personalizados según la aplicación
      let windowSize = DEFAULT_WINDOW_SIZE;
      let windowPosition = generateWindowPosition(windowCountRef.current);
      
      if (conversationId === 'bulk-send') {
        windowSize = { width: 1200, height: 650 };
        windowPosition = { x: 130, y: 60 };
      } else if (conversationId === 'templates') {
        windowSize = { width: 680, height: 640 };
        windowPosition = { x: 400, y: 80 };
      } else if (conversationId === 'advanced-config') {
        windowSize = { width: 1100, height: 550 };
        windowPosition = { x: 180, y: 80 };
      } else if (conversationId === 'stargate') {
        windowSize = { width: 1100, height: 550 };
        windowPosition = { x: 180, y: 80 };
      } else if (conversationId === 'scheduler') {
        windowSize = { width: 1100, height: 550 };
        windowPosition = { x: 180, y: 80 };
      } else if (conversationId === 'analytics') {
        windowSize = { width: 900, height: 500 };
        windowPosition = { x: 130, y: 80 };
      }

      // Usar registerWindow
      registerWindow({
        id: `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'conversation',
        conversationId,
        position: windowPosition,
        size: windowSize
      });
    },
    [windows, registerWindow, isWindowOpen]
  );

  /**
   * Cerrar ventana
   */
  const closeWindow = useCallback((windowId: string): void => {
    setWindows(prev => prev.filter(w => w.id !== windowId));
    console.log(`🗑️ Ventana cerrada: ${windowId}`);
  }, []);

  /**
   * Minimizar ventana (convertir en burbuja)
   */
  const minimizeWindow = useCallback((windowId: string): void => {
    setWindows(prev =>
      prev.map(w =>
        w.id === windowId
          ? {
              ...w,
              isMinimized: true,
              position: generateBubblePosition(),
              size: DEFAULT_BUBBLE_SIZE
            }
          : w
      )
    );
    console.log(`➖ Ventana minimizada: ${windowId}`);
  }, []);

  /**
   * Maximizar ventana (restaurar desde burbuja)
   */
  const maximizeWindow = useCallback((windowId: string): void => {
    setWindows(prev =>
      prev.map(w =>
        w.id === windowId
          ? {
              ...w,
              isMinimized: false,
              position: generateWindowPosition(windowCountRef.current),
              size: DEFAULT_WINDOW_SIZE,
              zIndex: nextZIndex
            }
          : w
      )
    );
    
    setNextZIndex(prev => prev + 1);
    console.log(`➕ Ventana maximizada: ${windowId}`);
  }, [nextZIndex]);

  /**
   * Actualizar posición de ventana (drag)
   */
  const updateWindowPosition = useCallback(
    (windowId: string, position: WindowPosition): void => {
      setWindows(prev =>
        prev.map(w =>
          w.id === windowId ? { ...w, position } : w
        )
      );
    },
    []
  );

  /**
   * Actualizar tamaño de ventana (resize)
   */
  const updateWindowSize = useCallback(
    (windowId: string, size: WindowSize): void => {
      setWindows(prev =>
        prev.map(w =>
          w.id === windowId ? { ...w, size } : w
        )
      );
    },
    []
  );

  /**
   * Traer ventana al frente (incrementar z-index)
   */
  const bringToFront = useCallback(
    (windowId: string): void => {
      const currentWindow = windows.find(w => w.id === windowId);
      
      // Si ya está al frente, no hacer nada
      if (currentWindow && currentWindow.zIndex === getMaxZIndex()) {
        return;
      }
      
      setWindows(prev =>
        prev.map(w =>
          w.id === windowId ? { ...w, zIndex: nextZIndex } : w
        )
      );
      
      setNextZIndex(prev => prev + 1);
      console.log(`🔝 Ventana traída al frente: ${windowId} - nuevo z-index: ${nextZIndex}`);
    },
    [nextZIndex, windows, getMaxZIndex]
  );

  /**
   * Cerrar todas las ventanas
   */
  const closeAllWindows = useCallback((): void => {
    setWindows([]);
    windowCountRef.current = 0;
    setNextZIndex(INITIAL_Z_INDEX);
    console.log('🗑️ Todas las ventanas cerradas');
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // RETURN
  // ───────────────────────────────────────────────────────────────────────

  return {
    windows,
    nextZIndex,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    updateWindowPosition,
    updateWindowSize,
    bringToFront,
    isWindowOpen,
    getWindow,
    closeAllWindows,
    registerWindow,      // ✅ NUEVO
    getMaxZIndex,        // ✅ NUEVO
    getActiveWindowId    // ✅ NUEVO
  };
};

export default useWindowManager;